import { ChangeDetectorRef, Component, ChangeDetectionStrategy } from "@angular/core";
import type { AnimationProp } from "@fortawesome/angular-fontawesome";
import { ActivatedRoute, Router } from "@angular/router";
import { ContentService, EventBusService, HelperService, UserService } from "@mypxplat/xplat/core";

import { LessonDetailBaseComponent } from "@mypxplat/xplat/features";
import { AppService, SeoService } from "@mypxplat/xplat/web/core";
import { takeUntil } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-lesson-detail",
  templateUrl: "lesson-detail.component.html",
  standalone: false,
})
export class LessonDetailComponent extends LessonDetailBaseComponent {
  public breadcrumbs: any[] = [];
  public loading: boolean;
  public loadingSaveLesson: boolean = false;
  public isSmallViewport: boolean = false;
  public lessonRuntimeLabel: string = "";
  public lessonVideoCountLabel: string = "";
  public lessonSnippetHtml: string = "";
  public lessonLibraryCtaLabel: string = "Add to My Library";
  public lessonLibraryIcon: string = "plus";
  public lessonLibraryIconAnimation: AnimationProp | undefined;
  private _hasScrolledToTop = false;

  constructor(
    userService: UserService,
    public contentService: ContentService,
    public activatedRoute: ActivatedRoute,
    public helperService: HelperService,
    public appService: AppService,
    public eventBusService: EventBusService,
    public router: Router,
    private _seo: SeoService,
    private _cd: ChangeDetectorRef
  ) {
    super(userService);
  }

  ngOnInit() {
    this.lessonId = this.activatedRoute.snapshot.params["id"];
    this.loading = true;
    this.isSmallViewport = this.appService.isSmallViewport;
    // Reset scroll from the previous Learn page (router does not restore position).
    this._scrollToTop();
    this.appService.viewportChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isSmallViewport = this.appService.isSmallViewport;
      this._cd.markForCheck();
    });
    this.init();
    this.eventBusService
      .observe(this.eventBusService.types.videoClosed)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.contentService.getEducationData().subscribe((result) => {
          this.init();
        });
      });
  }

  toggleSaveLesson(lesson: any) {
    if (!this.user) {
      this.appService.redirectToConnect(true, false);
      return;
    }
    this.loadingSaveLesson = true;
    this.lessonLibraryIcon = "spinner";
    this.lessonLibraryIconAnimation = "spin";
    this.contentService
      .saveUserLearnGroupData(lesson.id, {
        user_id: this.user.id,
        learn_group_id: lesson.id,
        enrolled: !this.contentService.userLearnData.lessons[lesson.id]?.enrolled,
      })
      .subscribe((result) => {
        this.init();
        this.loadingSaveLesson = false;
      });
  }

  init() {
    this.contentService.getEducationData().subscribe((result) => {
      this.lesson = this.contentService.lessonMap[this.lessonId];
      this.loading = false;
      this.lessonRuntimeLabel = this.lesson?.runtime != null ? this.helperService.hhmmss(this.lesson.runtime, true) : "";
      this.lessonVideoCountLabel = this.lesson?.videos ? `${this.lesson.videos.length} Videos` : "";
      this.lessonSnippetHtml = this.lesson?.snippet || this.lesson?.description || "";
      this._updateLibraryCtaState();
      this.breadcrumbs = [
        { label: "header.nav.home", routerLink: ["/home"] },
        { label: "fui-header.learn", routerLink: ["/learn"] },
        { label: "education.lessons", routerLink: ["/learn/lessons"] },
        { label: this.lesson?.title },
      ];
      this._applySeo();
      // Scroll again after content paints so we don't keep the Lessons mid-page offset.
      if (!this._hasScrolledToTop) {
        this._hasScrolledToTop = true;
        this._scrollToTop();
      }
    });
  }

  private _scrollToTop() {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const content = document.getElementById("sphere-app-content");
    if (content) {
      content.scrollTop = 0;
    }
  }

  private _updateLibraryCtaState() {
    const enrolled = !!this.contentService.userLearnData.lessons[this.lesson?.id]?.enrolled;
    this.lessonLibraryCtaLabel = enrolled ? "Remove from My Library" : "Add to My Library";
    this.lessonLibraryIcon = enrolled ? "minus" : "plus";
    this.lessonLibraryIconAnimation = undefined;
  }

  private _applySeo() {
    if (!this.lesson) return;
    const description = this.helperService.stripHtml ? this.helperService.stripHtml(this.lesson.description || "", true) : (this.lesson.description || "").replace(/<[^>]*>/g, "");
    const trimmed = description ? description.substring(0, 200).trim() : "A guitar lesson on MyFender.";
    const ogImage = this.lesson.image || undefined;
    const canonical = `https://my.fender.com/learn/lesson/${this.lesson.id}`;
    this._seo.setPageMeta({
      title: `${this.lesson.title} | MyFender Learn`,
      description: trimmed,
      canonical,
      ogImage,
      ogType: "article",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "LearningResource",
        name: this.lesson.title,
        description: trimmed,
        image: ogImage,
        url: canonical,
        learningResourceType: "Lesson",
        provider: {
          "@type": "Organization",
          name: "MyFender",
          url: "https://my.fender.com",
        },
      },
    });
  }

  pinLesson(enroll = true) {
    if (!this.user) {
      this.appService.redirectToConnect(true, false);
      return;
    }
    let args: any = {
      user_id: this.user.id,
      learn_group_id: this.lesson.id,
      enrolled: enroll,
      date_started: new Date(),
    };

    this.contentService.saveUserLearnGroupData(this.lesson.id, args).subscribe({
      next: (result) => {},
      error: (error) => {
        this.appService.alertError(error);
      },
    });
  }

  upgrade() {
    this.router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }
}
